import React, { useContext, useEffect, useState } from 'react';
import { Formik, Field } from 'formik';
import {
  SalesforceContext,
  Steps,
  AcquiringChannelFlags,
  CustomerTypes,
  WalletTypes,
  LockedFieldNames,
  ecommerceProductCategoryName,
} from 'src/context/salesforce-context';
import { OnboardingSummaryContext } from 'src/context/onboarding-summary-context';
import { formatCurrencyAmount } from 'src/helpers/formatting';
import { isVirtualDevice, isVirtualorInstoreDevice } from 'src/helpers/devices';
import SpinEditReskin from 'src/components/spin-edit-reskin/spin-edit-reskin';
import TealiumEvents from 'src/helpers/tealium-events';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import { ShowIf } from 'src/components';
import { PRODUCT_IMAGE_LIST } from 'src/helpers/constants';
import {
  DEVICE_NAMES,
  PRODUCT_IMAGE_MAP,
} from 'src/features/order/tech-debt/product-details-extra';
import './solution-summary.scss';

export default function SolutionSummary(props) {
  const [, setShouldLeftAlign] = useState(false);
  const [, setShowBankDetailsModal] = useState(false);

  const {
    merchant,
    terminals,
    uniqueTerminals,
    getDeviceOrderQuantity,
    setDeviceOrderQuantity,
    getDeviceDeliveryQuantity,
    customerType,
    lead,
    isVodapayChannel,
    setIsIncompleteSOVJourney,
    isVodacomBusinessChannel,
    isFieldLocked,
    isProductCategory,
    setAllDeliveriesInCategoryToZero,
  } = useContext(SalesforceContext);

  const {
    isChopChop,
    setDeviceIdToUpdate,
    setDeviceQuantityToUpdate,
    setDeviceQuantityIsManualUpdate,
    vatPercentage,
    totalNumberOfDevices,
    getDeliveriesToDisplay,
    setDevicePriceToUpdate,
    totalPrice,
    onceOffCost,
  } = useContext(OnboardingSummaryContext);

  const deliveriesToDisplay = getDeliveriesToDisplay();

  const getMinimumBusinessRequiredDevices = (terminalId) => getDeviceDeliveryQuantity(terminalId);

  const onDeviceQuantityChange = (
    terminalId,
    numberOfDevices,
    price,
    isManualUpdate,
    productCategory,
  ) => {
    const minimumDeviceQuantity = getMinimumBusinessRequiredDevices(terminalId);

    if (numberOfDevices < minimumDeviceQuantity) {
      setDeviceIdToUpdate(terminalId);
      setDeviceQuantityToUpdate(numberOfDevices);
      setDevicePriceToUpdate(price);
      setDeviceQuantityIsManualUpdate(isManualUpdate);
    } else {
      const deviceExistingQuantity = getDeviceOrderQuantity(terminalId);
      const deviceName = uniqueTerminals[terminalId].name;
      TealiumEvents.confirmTerminalQuantityChange(
        deviceName,
        price,
        deviceExistingQuantity,
        numberOfDevices,
        isManualUpdate,
        customerType,
      );
      setDeviceOrderQuantity(terminalId, numberOfDevices, price);
      if (numberOfDevices === 0 && isProductCategory(terminalId)) {
        setAllDeliveriesInCategoryToZero(productCategory);
      }
    }
  };

  const isQuantityFieldDisabled = isFieldLocked(
    LockedFieldNames.OPPORTUNITY_DELIVERIES_NUMBER_OF_DEVICES,
  );

  const getDeviceQuantitiesForFormik = () => {
    return Object.values(uniqueTerminals).reduce((accum, terminal) => {
      const deliveryInfo = merchant?.opportunity?.deliveries?.[terminal.id];
      const numberOfDevices = deliveryInfo ? deliveryInfo.number_of_devices : 0;
      const fieldName = `Quantity_Device_${terminal.id}`;
      accum[fieldName] = numberOfDevices;
      return accum;
    }, {});
  };

  const isOnlyProductEcommerceCategory = (products) =>
    products.every((product) => product.product_category === ecommerceProductCategoryName);

  const getBulletPointsFromText = (text) => {
    if (text) {
      return text.split(/\r?\n/).map((detail) => (
        <tr key={detail}>
          <td>{`${detail}`}</td>
        </tr>
      ));
    }

    return null;
  };

  const handleResize = () => {
    let element = document.getElementById('containerID');
    if (element) {
      if (element.getBoundingClientRect().height > 100) {
        setShouldLeftAlign(true);
      } else {
        setShouldLeftAlign(false);
      }
    }
  };

  const getSelectedProductsIcons = (productCategory) => {
    const productNames = getDeliveriesToDisplay()
      .filter(
        (delivery) =>
          delivery.product_category === productCategory && !delivery.is_product_category,
      )
      .map((product) => product.device_name);
    return productNames.map((productName) =>
      PRODUCT_IMAGE_LIST[productName]?.map((icon, index) => (
        <img
          src={icon}
          key={`${productName}+${index}`}
          className="has-padding-right-6 product-icon"
        />
      )),
    );
  };

  const getDeviceSectionDesktop = (
    index,
    deviceName,
    deviceId,
    deviceIdSalesforce,
    imageUrl,
    price,
    quantity,
    acquiringChannel,
    isSingleDevice,
    isProductCategory,
    productCategory,
  ) => {
    return (
      <Formik
        key={`${index} - ${deviceId}`}
        initialValues={getDeviceQuantitiesForFormik()}
        enableReinitialize={true}
      >
        {!props.isMobile && (
          <div>
            <span className="total-section">
              <div className="columns is-vcentered">
                <div className="column" />
              </div>
            </span>
            {index > 0 && <div className="separator-bar separator-bar-desktop" />}
            <div className="columns is-multiline section-for-device">
              <div className="column is-3">
                {imageUrl && (
                  <figure className="bgcolor-img">
                    <img src={imageUrl} />
                  </figure>
                )}
              </div>

              <div className="column is-4 device-information">
                <div className="text-block">
                  {isChopChop(deviceIdSalesforce) ? (
                    <div className="inner-text">
                      <p>VodaPay </p>
                      <p className="no-break">Chop-Chop</p>
                    </div>
                  ) : (
                    <p className="inner-text">{deviceName}</p>
                  )}
                </div>
                <div className="spaced-red" />
                <ShowIf condition={!isProductCategory && deviceName !== DEVICE_NAMES.TAP_ON_PHONE}>
                  <div className="text-block">
                    <p className="sub-heading">Devices shipped within</p>
                    <p className="shipping">5 - 7 business days</p>
                  </div>
                </ShowIf>
                <ShowIf condition={deviceName === DEVICE_NAMES.TAP_ON_PHONE}>
                  <div className="text-block">
                    <p className="sub-heading">Download from the Google PlayStore</p>
                  </div>
                </ShowIf>
                {!!isProductCategory && (
                  <div className="logo-box is-vcentered">
                    {/* function to compile selected card types' icons */}
                    {/* use the result of that function to map images */}
                    {/* add max width to image container and add padding right to images */}
                    {getSelectedProductsIcons(productCategory)}
                  </div>
                )}
              </div>

              <div className="column is-5 device-price-info">
                {!isSingleDevice && (
                  <div
                    className={`button remove-button-no-border is-pulled-right remove-icon-your-solution-desktop`}
                    onClick={() => {
                      onDeviceQuantityChange(deviceId, 0, price, true, productCategory);
                    }}
                  />
                )}
                {(isVirtualDevice(acquiringChannel) ||
                  deviceName === DEVICE_NAMES.TAP_ON_PHONE) && (
                  <p className="inner-text is-pulled-right has-padding-right-5">Free</p>
                )}
                <ShowIf
                  condition={
                    price > -1 &&
                    acquiringChannel !== AcquiringChannelFlags.vodapay &&
                    !isVodacomBusinessChannel &&
                    acquiringChannel !== AcquiringChannelFlags.buyInstoreFicaOnline
                  }
                >
                  <p className="inner-text is-pulled-right has-padding-right-5">
                    {acquiringChannel === AcquiringChannelFlags.buyOnlineFicaOnline ? (
                      <>
                        R{' '}
                        {formatCurrencyAmount(price * quantity + price * quantity * vatPercentage)}
                        <div className="vat-detail">incl VAT</div>
                      </>
                    ) : (
                      <>
                        R{' '}
                        {formatCurrencyAmount(price * quantity + price * quantity * vatPercentage)}
                        <div className="vat-detail">/ month incl VAT</div>
                      </>
                    )}
                  </p>
                </ShowIf>
                <ShowIf condition={acquiringChannel === AcquiringChannelFlags.buyInstoreFicaOnline}>
                  <p className="inner-text is-pulled-right has-padding-right-5">Pay in store</p>
                </ShowIf>

                {!isVirtualDevice(acquiringChannel) && !(isSingleDevice && isProductCategory) && (
                  <div className="input-spin-edit">
                    <span
                      automationid={`Onboarding_Create_Account_Confirm_Your_Solution_Dropdown_Quantity_For_Device_${deviceId}`}
                    >
                      <Field
                        name={`Quantity_Device_${deviceId}`}
                        automationid={`Onboarding_SpinEdit_Quantity_${deviceId}`}
                        component={SpinEditReskin}
                        min={totalNumberOfDevices === 1 ? 1 : 0}
                        max={!!isProductCategory ? 1 : 70}
                        disabled={isQuantityFieldDisabled}
                        onModified={(newNumber, isManualUpdate) =>
                          onDeviceQuantityChange(
                            deviceId,
                            newNumber,
                            price,
                            isManualUpdate,
                            productCategory,
                          )
                        }
                      />
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="space-quarter" />
          </div>
        )}
      </Formik>
    );
  };

  const getDeviceSectionMobile = (
    index,
    deviceName,
    deviceId,
    deviceIdSalesforce,
    imageUrl,
    price,
    setContains,
    deviceInformation,
    quantity,
    acquiringChannel,
    isSingleDevice,
    product_category,
  ) => {
    return (
      <div key={`${index} - ${deviceId}`} className="has-padding-bottom-1">
        {index > 0 && <div className="separator-bar" />}
        <div className="columns is-mobile">
          <div className="column centered is-6 has-mobile-padding-left-4">
            {imageUrl && (
              <figure className="bgcolor-img-mobile">
                <img src={imageUrl} />
              </figure>
            )}
          </div>

          <div className="column is-4">
            <div className="centered">
              <div className="text-block left-aligned">
                {index === 0 && <p className="sub-heading"></p>}
                {isChopChop(deviceIdSalesforce) ? (
                  <div className="mobile-your-solutions-heading">
                    <p>VodaPay </p>
                    <p className="no-break">Chop-Chop</p>
                  </div>
                ) : (
                  <p className="mobile-your-solutions-heading">{deviceName}</p>
                )}
                <p className="mobile-your-solutions-heading_qty">QTY: {quantity}</p>
              </div>
            </div>
          </div>

          <div className="column is-2 has-padding-top-0">
            <div className="container">
              <div className="centered">
                {!isSingleDevice && (
                  <span
                    className={`button remove-button-no-border is-pulled-right remove-icon-your-solution`}
                    onClick={() => {
                      if (
                        isVodacomBusinessChannel &&
                        merchant.account.settlements_method === WalletTypes.SOV
                      ) {
                        // show modal for adding bank details in case you
                        setShowBankDetailsModal(true);
                      } else {
                        onDeviceQuantityChange(deviceId, 0, 0, true, product_category);
                      }
                    }}
                    automationid={`Onboarding_Create_Account_Confirm_Your_Solution_Paragraph_Set_Quantity_for_Device_${deviceId}_To_Zero`}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="spaced-red-mobile" />
        {setContains && (
          <div className="text-block mobile-text-block">
            <p className="sub-heading mobile-text-subheading">What you get in the box</p>
            <div className="text-body mobile-text">
              <table>
                <tbody>{setContains}</tbody>
              </table>
            </div>
          </div>
        )}
        {!isVodacomBusinessChannel &&
          (!isVirtualorInstoreDevice(acquiringChannel) ? (
            <div className="columns has-padding-left-0 has-mobile-margin-left-7">
              <div className="column mobile-price-subheading">Unit price</div>
              <div className="column mobile-price-text-area">{`R ${formatCurrencyAmount(
                price,
              )}`}</div>

              {acquiringChannel !== AcquiringChannelFlags.buyOnlineFicaOnline && (
                <>
                  <div className="column mobile-price-subheading">VAT</div>
                  <div className="column mobile-price-text-area">
                    {`R ${formatCurrencyAmount(price * quantity * vatPercentage)}`}
                  </div>
                </>
              )}

              {acquiringChannel !== AcquiringChannelFlags.buyOnlineFicaOnline ? (
                <>
                  <div className="column mobile-price-subheading">Sub total (VAT Incl.)</div>
                  <div className="column mobile-price-text-area">
                    {`R ${formatCurrencyAmount(
                      price * quantity + price * quantity * vatPercentage,
                    )}`}
                  </div>
                </>
              ) : (
                <>
                  <div className="column mobile-price-subheading">Sub total (VAT Incl.)</div>
                  <div className="column mobile-price-text-area">
                    {`R ${formatCurrencyAmount(
                      price * quantity + price * quantity * vatPercentage,
                    )} once off`}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="columns has-padding-left-0 has-mobile-margin-left-7">
              {/* <div className="column mobile-price-subheading">Unit price</div> */}
              <div className="column mobile-price-text-area">Pay in store</div>
            </div>
          ))}
        <div className="space-quarter" />
      </div>
    );
  };

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, deliveriesToDisplay);

  return (
    <div
      className="SolutionSummary input-area-container mobile-margin-bottom-0"
      data-testid="SolutionSummary"
    >
      <div className={`${!props.isMobile ? 'container is-fluid' : 'solution-is-mobile'}`}>
        {!props.isMobile && (
          <div className="input-area-container-heading padding-bottom-25 has-text-left your-solution-heading">
            <div>
              {(customerType === CustomerTypes.normal &&
                lead.consumerType === CONSUMER_TYPE.owner) ||
              customerType === CustomerTypes.pos
                ? 'Your solutions'
                : 'Customer solutions'}
              {!isVodapayChannel && !isVodacomBusinessChannel && (
                <span
                  className="edit-button is-pulled-right"
                  onClick={() => {
                    setIsIncompleteSOVJourney(false);
                    props.quickNavigate(Steps.CHOOSE_A_SOLUTION);
                  }}
                  automationid="Onboarding_Create_Account_Confirm_Your_Solution_Span_Edit_Navigate_to_Choose_Solution"
                />
              )}
            </div>
          </div>
        )}
        <div className="is-hidden-mobile-new">
          {deliveriesToDisplay
            .filter((terminal) => !terminal.product_category || !!terminal.is_product_category)
            .map((delivery, index) => {
              const deviceId = delivery.device_id;
              const deviceName = delivery.device_name;
              const price = delivery.price;
              const deviceIdSalesforce = delivery.deviceIdSalesforce;
              const terminalInfo = terminals[deviceIdSalesforce];
              const imageUrl = PRODUCT_IMAGE_MAP[terminalInfo.name];
              const quantity = getDeviceOrderQuantity(deviceId);
              const isSingleDevice =
                deliveriesToDisplay.length === 1 ||
                isOnlyProductEcommerceCategory(deliveriesToDisplay);
              return getDeviceSectionDesktop(
                index,
                deviceName,
                deviceId,
                deviceIdSalesforce,
                imageUrl,
                price,
                quantity,
                delivery.acquiring_channel,
                isSingleDevice,
                delivery.is_product_category,
                delivery.product_category,
              );
            })}
        </div>

        <div className="is-hidden-not-mobile">
          {deliveriesToDisplay
            .filter((terminal) => !terminal.product_category || !!terminal.is_product_category)
            .map((delivery, index) => {
              const deviceId = delivery.device_id;
              const deviceName = delivery.device_name;
              const price = delivery.price;
              const deviceIdSalesforce = delivery.deviceIdSalesforce;
              const terminalInfo = terminals[deviceIdSalesforce];
              const imageUrl = PRODUCT_IMAGE_MAP[terminalInfo.name];
              const setContains = getBulletPointsFromText(terminalInfo.set_contains);
              const deviceInformation = getBulletPointsFromText(terminalInfo.device_information);
              const quantity = getDeviceOrderQuantity(deviceId).toString();
              const isSingleDevice = deliveriesToDisplay.length === 1;
              return getDeviceSectionMobile(
                index,
                deviceName,
                deviceId,
                deviceIdSalesforce,
                imageUrl,
                price,
                setContains,
                deviceInformation,
                quantity,
                delivery.acquiring_channel,
                isSingleDevice,
                delivery.product_category,
              );
            })}
          {!isVodacomBusinessChannel && (
            <div className="columns is-mobile is-vcentered">
              <div className=" column is-pulled-right right-align-text is-full has-padding-bottom-0">
                <span>
                  <div className="text-body">
                    <p className="sub-heading solution-text-footer">Order total</p>
                    <p
                      automationid={`Onboarding_Create_Account_Confirm_Your_Solution_Paragraph_Total`}
                      className="price-solution-footer"
                    >
                      R {formatCurrencyAmount(totalPrice)} / month
                    </p>
                    {onceOffCost > 0 && (
                      <p
                        automationid={`Onboarding_Create_Account_Confirm_Your_Solution_Paragraph_Total_once_off`}
                        className="price-solution-footer"
                      >
                        {`R ${formatCurrencyAmount(onceOffCost)} once off`}
                      </p>
                    )}
                  </div>
                </span>
                <br />
              </div>
            </div>
          )}
          <div className="spacer-solution-summary" />
        </div>
      </div>
    </div>
  );
}
